import React, { useState } from "react";
import {
	Box,
	Button,
	FormControl,
	FormLabel,
	Input,
	Textarea,
	VStack,
	HStack,
	Tag,
	TagLabel,
	TagCloseButton,
	Text,
} from "@chakra-ui/react";
import KnowledgeBaseArticle, { UpdateArticleRequest } from "../../../models/KnowledgeBaseArticle";

interface ArticleEditorProps {
	article: KnowledgeBaseArticle;
	onSave: (updates: UpdateArticleRequest) => void;
	onCancel: () => void;
	isSaving: boolean;
}

/**
 * Component for editing knowledge base articles.
 * Allows editors to modify title, content, tags, and image URLs.
 */
const ArticleEditor: React.FC<ArticleEditorProps> = ({ article, onSave, onCancel, isSaving }) => {
	const [title, setTitle] = useState(article.title);
	const [content, setContent] = useState(article.content);
	const [tags, setTags] = useState<string[]>(article.tags);
	const [imageUrls, setImageUrls] = useState<string[]>(article.imageUrls);
	const [newTag, setNewTag] = useState("");
	const [newImageUrl, setNewImageUrl] = useState("");

	const handleAddTag = () => {
		if (newTag.trim() && !tags.includes(newTag.trim())) {
			setTags([...tags, newTag.trim()]);
			setNewTag("");
		}
	};

	const handleRemoveTag = (tagToRemove: string) => {
		setTags(tags.filter((tag) => tag !== tagToRemove));
	};

	const handleAddImageUrl = () => {
		if (newImageUrl.trim()) {
			setImageUrls([...imageUrls, newImageUrl.trim()]);
			setNewImageUrl("");
		}
	};

	const handleRemoveImageUrl = (index: number) => {
		setImageUrls(imageUrls.filter((_, i) => i !== index));
	};

	const handleSave = () => {
		const updates: UpdateArticleRequest = {};

		if (title !== article.title) updates.title = title;
		if (content !== article.content) updates.content = content;
		if (JSON.stringify(tags) !== JSON.stringify(article.tags)) updates.tags = tags;
		if (JSON.stringify(imageUrls) !== JSON.stringify(article.imageUrls))
			updates.imageUrls = imageUrls;

		onSave(updates);
	};

	const hasChanges =
		title !== article.title ||
		content !== article.content ||
		JSON.stringify(tags) !== JSON.stringify(article.tags) ||
		JSON.stringify(imageUrls) !== JSON.stringify(article.imageUrls);

	return (
		<Box>
			<VStack spacing={4} align="stretch">
				{/* Title */}
				<FormControl>
					<FormLabel>Titel</FormLabel>
					<Input
						value={title}
						onChange={(e) => setTitle(e.target.value)}
						placeholder="Artikeltitel"
					/>
				</FormControl>

				{/* Tags */}
				<FormControl>
					<FormLabel>Taggar</FormLabel>
					<HStack mb={2} flexWrap="wrap">
						{tags.map((tag) => (
							<Tag key={tag} size="md" colorScheme="blue" mb={2}>
								<TagLabel>{tag}</TagLabel>
								<TagCloseButton onClick={() => handleRemoveTag(tag)} />
							</Tag>
						))}
					</HStack>
					<HStack>
						<Input
							value={newTag}
							onChange={(e) => setNewTag(e.target.value)}
							placeholder="Lägg till tagg (t.ex. bokningstyp)"
							onKeyPress={(e) => e.key === "Enter" && handleAddTag()}
						/>
						<Button onClick={handleAddTag} size="sm">
							Lägg till
						</Button>
					</HStack>
				</FormControl>

				{/* Content */}
				<FormControl>
					<FormLabel>Innehåll (Markdown)</FormLabel>
					<Textarea
						value={content}
						onChange={(e) => setContent(e.target.value)}
						placeholder="Artikelinnehåll i markdown-format"
						rows={15}
						fontFamily="monospace"
					/>
					<Text fontSize="xs" color="gray.500" mt={1}>
						Stödjer: # Rubrik, ## Underrubrik, **fetstil**, *kursiv*, - punktlista
					</Text>
				</FormControl>

				{/* Image URLs - Hidden for now, will be enabled later */}
				{/* <FormControl>
					<FormLabel>Bild-URLer</FormLabel>
					{imageUrls.length > 0 && (
						<VStack align="stretch" mb={2} spacing={1}>
							{imageUrls.map((url, index) => (
								<HStack key={index}>
									<Text fontSize="sm" flex={1} noOfLines={1}>
										{url}
									</Text>
									<Button size="xs" colorScheme="red" onClick={() => handleRemoveImageUrl(index)}>
										Ta bort
									</Button>
								</HStack>
							))}
						</VStack>
					)}
					<HStack>
						<Input
							value={newImageUrl}
							onChange={(e) => setNewImageUrl(e.target.value)}
							placeholder="https://example.com/bild.png"
							onKeyPress={(e) => e.key === "Enter" && handleAddImageUrl()}
						/>
						<Button onClick={handleAddImageUrl} size="sm">
							Lägg till
						</Button>
					</HStack>
				</FormControl> */}

				{/* Action Buttons */}
				<HStack justify="flex-end" pt={4} borderTop="1px solid" borderColor="gray.200">
					<Button variant="ghost" onClick={onCancel} isDisabled={isSaving}>
						Avbryt
					</Button>
					<Button
						colorScheme="blue"
						onClick={handleSave}
						isDisabled={!hasChanges}
						isLoading={isSaving}
					>
						Spara ändringar
					</Button>
				</HStack>
			</VStack>
		</Box>
	);
};

export default ArticleEditor;
